import React, { useEffect, useMemo, useState } from 'react';
import { markOrderAsShipped, resolveOrderShippingAddress, subscribeToAllOrders, formatOrderNumber, type Order, type ShippingAddress } from '../../../utils/firebase';
import { AlertCircle, ArrowUpDown, CheckCircle2, ChevronDown, Download, Package, Search, Truck } from 'lucide-react';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
function isPhysicalOrder(order: Order): boolean {
  return order.lineItems.some(item => item.type === 'supplement');
}
function fulfillmentLabel(status: Order['fulfillmentStatus']): string {
  if (status === 'shipped') return 'Completed';
  return status.replace('_', ' ');
}
function csvCell(value: string | number | undefined): string {
  const raw = String(value ?? '');
  return `"${raw.replace(/"/g, '""')}"`;
}
function downloadShippingCsv(orders: Order[]) {
  const headers = ['Email', 'Name', 'Company Address', 'Address', 'Line 2', 'City', 'State', 'Zipcode', 'Country', 'Order ID', 'Order Items', 'Pounds', 'Length', 'Width', 'Height'];
  const rows = orders.map(order => {
    const address = order.shippingAddress;
    const items = order.lineItems.filter(item => item.type === 'supplement').map(item => `${item.name} × ${item.quantity}`).join('; ');
    return [order.email, address?.recipientName, address?.company, address?.line1, address?.line2, address?.city, address?.state, address?.postalCode, address?.country, order.id, items, '', '', '', ''].map(csvCell).join(',');
  });
  const csv = [headers.map(csvCell).join(','), ...rows].join('\n');
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `shipping-orders-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
export function AdminDashboard() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [shippingOrderId, setShippingOrderId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [legacyOrderAddresses, setLegacyOrderAddresses] = useState<Record<string, ShippingAddress>>({});
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Order | 'date';
    direction: 'asc' | 'desc';
  }>({
    key: 'date',
    direction: 'desc'
  });
  useEffect(() => {
    const unsub = subscribeToAllOrders(data => {
      setOrders(data);
      setLoading(false);
    });
    return unsub;
  }, []);
  useEffect(() => {
    let active = true;
    const legacyOrders = orders.filter(order => isPhysicalOrder(order) && !order.shippingAddress?.line1);
    if (legacyOrders.length === 0) {
      setLegacyOrderAddresses({});
      return;
    }
    Promise.all(legacyOrders.map(async order => [order.id, await resolveOrderShippingAddress(order)] as const)).then(entries => {
      if (!active) return;
      const next: Record<string, ShippingAddress> = {};
      entries.forEach(([orderId, address]) => {
        if (address) next[orderId] = address;
      });
      setLegacyOrderAddresses(next);
    }).catch(error => {
      console.error('[Admin] Could not load legacy shipping addresses:', error);
      if (active) setLegacyOrderAddresses({});
    });
    return () => {
      active = false;
    };
  }, [orders]);
  const resolvedOrders = useMemo(() => orders.map(order => ({
    ...order,
    shippingAddress: order.shippingAddress?.line1 ? order.shippingAddress : legacyOrderAddresses[order.id]
  })), [orders, legacyOrderAddresses]);
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let totalRevenue = 0;
    let revenueToday = 0;
    let ordersToday = 0;
    let pending = 0;
    let shipped = 0;
    let digital = 0;
    let physical = 0;
    orders.forEach(o => {
      if (o.status === 'paid') {
        totalRevenue += o.total;
        const orderDate = new Date(o.createdAt);
        if (orderDate >= today) {
          revenueToday += o.total;
          ordersToday++;
        }
      }
      if (o.fulfillmentStatus === 'pending') pending++;
      if (o.fulfillmentStatus === 'shipped') shipped++;
      const hasDigital = o.lineItems.some(i => i.type === 'book');
      const hasPhysical = o.lineItems.some(i => i.type === 'supplement');
      if (hasDigital) digital++;
      if (hasPhysical) physical++;
    });
    return {
      totalOrders: orders.length,
      ordersToday,
      revenueToday,
      totalRevenue,
      pending,
      shipped,
      digital,
      physical
    };
  }, [orders]);
  const filteredOrders = useMemo(() => {
    return resolvedOrders.filter(o => {
      const matchesSearch = o.id.toLowerCase().includes(search.toLowerCase()) || o.email.toLowerCase().includes(search.toLowerCase()) || (o.shippingAddress?.recipientName || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || o.fulfillmentStatus === statusFilter;
      const hasDigital = o.lineItems.some(i => i.type === 'book');
      const hasPhysical = o.lineItems.some(i => i.type === 'supplement');
      const matchesType = typeFilter === 'all' || typeFilter === 'digital' && hasDigital || typeFilter === 'physical' && hasPhysical;
      return matchesSearch && matchesStatus && matchesType;
    }).sort((a, b) => {
      if (sortConfig.key === 'date') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      // Add other sort logic if needed
      return 0;
    });
  }, [resolvedOrders, search, statusFilter, typeFilter, sortConfig]);
  const handleSort = (key: typeof sortConfig.key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  const selectableOrders = filteredOrders.filter(order => isPhysicalOrder(order) && Boolean(order.shippingAddress));
  const selectedOrders = filteredOrders.filter(order => selectedOrderIds.has(order.id));
  const allSelectableSelected = selectableOrders.length > 0 && selectableOrders.every(order => selectedOrderIds.has(order.id));
  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds(current => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);else next.add(orderId);
      return next;
    });
  };
  const toggleAllSelections = () => {
    setSelectedOrderIds(current => {
      const next = new Set(current);
      if (allSelectableSelected) {
        selectableOrders.forEach(order => next.delete(order.id));
      } else {
        selectableOrders.forEach(order => next.add(order.id));
      }
      return next;
    });
  };
  const handleMarkShipped = async (orderId: string) => {
    setActionError(null);
    setShippingOrderId(orderId);
    try {
      await markOrderAsShipped(orderId);
    } catch {
      setActionError('The fulfillment status could not be updated. Confirm the admin update rule is published, then try again.');
    } finally {
      setShippingOrderId(null);
    }
  };
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-159" data-mp="389vq6i8b4qe2">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-160" data-mp="2kn5ndeo4wrse" />
      </div>;
  }
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-161" data-mp="2fky92ifu0zmw">
      <h1 className="text-2xl font-bold text-[#111111] mb-8" data-id="element-162" data-mp="2uc71ezb4cz3q">
        Dashboard Overview
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-163" data-mp="2pjmcab35ra6j">
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-164" data-mp="21ul0qez13ix1">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-165" data-mp="1wxbdn5z43jcd">
            Total Revenue
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-166" data-mp="ce90g6zo43mi">
            {formatPrice(stats.totalRevenue)}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-167" data-mp="h6iiz1yge1er">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-168" data-mp="3f42p2vxkebe">
            Revenue Today
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-169" data-mp="7fxljhuin2xf">
            {formatPrice(stats.revenueToday)}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-170" data-mp="10439edgv981h">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-171" data-mp="101z5hviyw917">
            Total Orders
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-172" data-mp="m6n10wf0f473">
            {stats.totalOrders}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-173" data-mp="36usz7hq5acxh">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-174" data-mp="36wvvgjhw52an">
            Orders Today
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-175" data-mp="1bogggf3llxwm">
            {stats.ordersToday}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-id="element-176" data-mp="1fqbijltcsfbg">
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-177" data-mp="11yojxfbxuupi">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-178" data-mp="11wkid41ii7se">
            Pending
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-179" data-mp="s0qxmxdn37zj">{stats.pending}</p>
        </div>
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-180" data-mp="wzkcat4nx51a">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-181" data-mp="wxhc5g9spviu">
            Shipped
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-182" data-mp="nyvz20n3f8pg">{stats.shipped}</p>
        </div>
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-183" data-mp="o0qi19et0qqw">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-184" data-mp="2deljdxhekx0z">
            Digital
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-185" data-mp="2sgv51is9x64j">{stats.digital}</p>
        </div>
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-186" data-mp="2sehcbgl2g605">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-187" data-mp="2njlj4d43fclg">
            Physical
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-188" data-mp="31b058startwq">{stats.physical}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-189" data-mp="3b7cv1flq1nm7">
        {/* Toolbar */}
        <div className="p-4 border-b border-[#E5E5E5] flex flex-col sm:flex-row gap-4 justify-between items-center bg-[#FAFAFA]" data-id="element-190" data-mp="368rtncuqgxw6">
          <div className="relative w-full sm:w-96" data-id="element-191" data-mp="3k6q78g18cqam">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-192" data-mp="3g6fzaxtvnpt4" />
            <input type="text" placeholder="Search orders, customers, emails..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-193" data-mp="1pu0dbyav3h38" />
          </div>
          <div className="flex gap-3 w-full sm:w-auto" data-id="element-194" data-mp="3uvck6j3mijj2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate" data-id="element-195" data-mp="3utrogq5koujm">
              <option value="all" data-id="element-196" data-mp="coofguchjydx">All Statuses</option>
              <option value="pending" data-id="element-197" data-mp="hgg592nti030">Pending</option>
              <option value="shipped" data-id="element-198" data-mp="hhr8909fv3t2">Shipped</option>
              <option value="delivered" data-id="element-199" data-mp="mf9lv3yei77e">Delivered</option>
              <option value="digital_delivered" data-id="element-200" data-mp="merg9gpumgmt">Digital Delivered</option>
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate" data-id="element-201" data-mp="rg72hc3rit6s">
              <option value="all" data-id="element-202" data-mp="2bvlkz8m0zggc">All Types</option>
              <option value="physical" data-id="element-203" data-mp="2bz118erfqvjc">Physical</option>
              <option value="digital" data-id="element-204" data-mp="26yd0v2hbh2cg">Digital</option>
            </select>
          </div>
        </div>

        {selectedOrders.length > 0 && <div className="px-4 py-3 border-b border-[#D8E4E6] bg-[#F0F8F8] flex flex-wrap items-center justify-between gap-3" data-id="element-205" data-mp="220tlfz8vujix">
            <p className="text-sm font-medium text-[#17474B]" data-id="element-206" data-mp="2umml3zyvti1h">
              {selectedOrders.length} {selectedOrders.length === 1 ? 'order' : 'orders'} selected for shipping export
            </p>
            <button type="button" onClick={() => downloadShippingCsv(selectedOrders)} className="inline-flex items-center gap-2 px-4 py-2 bg-tealslate text-white rounded-md text-sm font-bold hover:bg-navyslate transition-colors focus:outline-none focus:ring-2 focus:ring-tealslate focus:ring-offset-2" data-id="element-207" data-mp="2kxuxg40utjwd">
              <Download className="w-4 h-4" data-id="element-208" data-mp="2y63ku59jnod9" /> Export shipping CSV
            </button>
          </div>}

        {actionError && <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 text-sm text-red-800 rounded-md flex items-start gap-2" data-id="element-209" data-mp="2o6cm1rdyan7d">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-210" data-mp="2t5p9oecnbyb0" />
            {actionError}
          </div>}

        <div className="overflow-x-auto" data-id="element-211" data-mp="3gva53wpzko16">
          <table className="w-full text-left border-collapse" data-id="element-212" data-mp="3ltbmmd7rkfaj">
            <thead data-id="element-213" data-mp="3lvo30lxvihlh">
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-214" data-mp="374oybm05sd76">
                <th className="w-12 px-4 py-4" data-id="element-215" data-mp="374f7jw7ai20f">
                  <input type="checkbox" checked={allSelectableSelected} onChange={toggleAllSelections} disabled={selectableOrders.length === 0} aria-label="Select all shippable orders" className="w-4 h-4 rounded border-[#B8B8B8] text-tealslate focus:ring-tealslate disabled:opacity-40" data-id="element-216" data-mp="3c38jhk6xhkyh" />
                </th>
                <th className="px-4 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-217" data-mp="1o5r16z6krbg9">
                  Order
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider cursor-pointer hover:text-[#111111]" onClick={() => handleSort('date')} data-id="element-218" data-mp="1xw2db90ewqy8">
                  <div className="flex items-center gap-1" data-id="element-219" data-mp="1586m2jtm3r8u">
                    Date <ArrowUpDown className="w-3 h-3" data-id="element-220" data-mp="1j6o7gaw1ornl" />
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-221" data-mp="1e835o7159zx1">
                  Customer
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-222" data-mp="2uki6df2qdig3">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-223" data-mp="1n9u8zpic5om5">
                  Total
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-224" data-mp="1s7c68imzqfeb">
                  Payment
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-225" data-mp="218add9sq7314">
              {filteredOrders.length === 0 ? <tr data-id="element-226" data-mp="14kdu1x5wltm7">
                  <td colSpan={7} className="px-6 py-12 text-center text-[#5A5A5A]" data-id="element-227" data-mp="14kxbzdvk0i08">
                    No orders found matching your filters.
                  </td>
                </tr> : filteredOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const canExport = isPhysicalOrder(order) && Boolean(order.shippingAddress);
              const address = order.shippingAddress;
              return <React.Fragment key={order.id} data-id="element-228" data-mp="iaxd4j8xhwb8">
                      <tr className={isExpanded ? 'bg-[#F7FAFA]' : 'hover:bg-[#FAFAFA] transition-colors'} data-id="element-229" data-mp="i8b5kjm1fgt0">
                        <td className="px-4 py-4 align-top" data-id="element-230" data-mp="8chk2aqcccz3">
                          <input type="checkbox" checked={selectedOrderIds.has(order.id)} onChange={() => toggleOrderSelection(order.id)} disabled={!canExport} aria-label={`Select order ${order.id} for shipping export`} className="w-4 h-4 rounded border-[#B8B8B8] text-tealslate focus:ring-tealslate disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-231" data-mp="8awnygodolz4" />
                        </td>
                        <td className="px-4 py-4" data-id="element-232" data-mp="3cqz90ei8mt5b">
                          <button type="button" onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} aria-expanded={isExpanded} className="text-left inline-flex items-center gap-2 text-tealslate font-bold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-tealslate rounded-sm" data-id="element-233" data-mp="3csiow3zzf26k">
                            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-234" data-mp="uishti4nmmfx" />
                            #{formatOrderNumber(order.id)}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#5A5A5A]" data-id="element-235" data-mp="ll89jp8djpvt">
                          {formatDate(order.createdAt)}
                        </td>
                        <td className="px-6 py-4" data-id="element-236" data-mp="budmiinb0m9b">
                          <p className="text-sm font-medium text-[#111111]" data-id="element-237" data-mp="3tyytt2aymbzp">
                            {address?.recipientName || (isPhysicalOrder(order) ? 'Shipping details unavailable' : 'Digital customer')}
                          </p>
                          <p className="text-xs text-[#5A5A5A]" data-id="element-238" data-mp="3adyiwlarlg8z">{order.email}</p>
                        </td>
                        <td className="px-6 py-4" data-id="element-239" data-mp="3odzpmhdpnhwd">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${order.fulfillmentStatus === 'pending' ? 'bg-amber-100 text-amber-800' : order.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-240" data-mp="3kbcqdvepmup5">
                            {fulfillmentLabel(order.fulfillmentStatus)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-[#111111]" data-id="element-241" data-mp="6ysvcxo82vos">
                          {formatPrice(order.total)}
                        </td>
                        <td className="px-6 py-4" data-id="element-242" data-mp="fypq9h17gqsa">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-243" data-mp="sfl546ms635r">
                            {order.status}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && <tr className="bg-[#F7FAFA]" data-id="element-244" data-mp="9pwnff7xwj8c">
                          <td colSpan={7} className="px-6 py-6 border-t border-[#E5E5E5]" data-id="element-245" data-mp="4qjzer5c1646">
                            <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6" data-id="element-246" data-mp="jewpx8lnhwra">
                              <section data-id="element-247" data-mp="eog8zeimft06">
                                <div className="flex items-center gap-2 mb-4" data-id="element-248" data-mp="enofc98l45cj">
                                  <Package className="w-4 h-4 text-tealslate" data-id="element-249" data-mp="3n3hwmqaqpkun" />
                                  <h2 className="text-sm font-bold text-[#111111]" data-id="element-250" data-mp="3i5gd649ejjin">Items purchased</h2>
                                </div>
                                <ul className="divide-y divide-[#E5E5E5] bg-white border border-[#E5E5E5] rounded-lg" data-id="element-251" data-mp="1xmclb55e0tw6">
                                  {order.lineItems.map((item, index) => <li key={`${item.productId}-${index}`} className="px-4 py-3 flex items-center justify-between gap-4 text-sm" data-id="element-252" data-mp="1onr69jz4jx05" data-mpi={index}>
                                      <div data-id="element-253" data-mp="1sloyyws3t18a">
                                        <p className="font-medium text-[#111111]" data-id="element-254" data-mp="2geoyyo1hrhlm">{item.name}</p>
                                        <p className="text-xs text-[#5A5A5A] capitalize" data-id="element-255" data-mp="2lbnp6vi1b4ej">{item.type}</p>
                                      </div>
                                      <span className="text-[#5A5A5A] shrink-0" data-id="element-256" data-mp="27fa83nhex50l">Qty {item.quantity}</span>
                                    </li>)}
                                </ul>
                              </section>

                              <section className="bg-white border border-[#E5E5E5] rounded-lg p-5" data-id="element-257" data-mp="10xwxbdhxesl6">
                                <h2 className="text-sm font-bold text-[#111111] mb-4" data-id="element-258" data-mp="32kpyutl8fvsj">Delivery address</h2>
                                {address ? <address className="not-italic text-sm text-[#5A5A5A] leading-6 mb-5" data-id="element-259" data-mp="2sw82cojzkaup">
                                    <p className="font-medium text-[#111111]" data-id="element-260" data-mp="2ouw8u7xzr5h8">{address.recipientName || 'Recipient not provided'}</p>
                                    {address.company && <p data-id="element-261" data-mp="2jx4i3h931pem">{address.company}</p>}
                                    <p data-id="element-262" data-mp="2evx8dcndqg5b">{address.line1}</p>
                                    {address.line2 && <p data-id="element-263" data-mp="29xdpxm7zvmhp">{address.line2}</p>}
                                    <p data-id="element-264" data-mp="29xvqgv1owmvf">{address.city}, {address.state} {address.postalCode}</p>
                                    <p data-id="element-265" data-mp="25xcx2wk5f8r4">{address.country}</p>
                                    {address.deliveryInstructions?.trim() && <div className="mt-4 pt-4 border-t border-[#E5E5E5]" data-id="element-266" data-mp="44fahigytcem">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1" data-id="element-267" data-mp="42kmfvaed46b">
                                          Delivery instructions
                                        </p>
                                        <p className="whitespace-pre-line text-[#3B3B3B]" data-id="element-268" data-mp="1vrlfsa0p4vzd">
                                          {address.deliveryInstructions}
                                        </p>
                                      </div>}
                                  </address> : isPhysicalOrder(order) ? <p className="text-sm text-[#5A5A5A] mb-5" data-id="element-269" data-mp="1c3cnlzz5kpzv">This is a physical order, but its delivery address was not saved with the order. It cannot be included in a shipping CSV until an address is available.</p> : <p className="text-sm text-[#5A5A5A] mb-5" data-id="element-270" data-mp="2nldp0uiyhh8z">This is a digital guide order and does not require delivery.</p>}
                                {order.fulfillmentStatus === 'pending' && isPhysicalOrder(order) && <button type="button" onClick={() => handleMarkShipped(order.id)} disabled={shippingOrderId === order.id} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-tealslate text-white rounded-md text-sm font-bold hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-tealslate focus:ring-offset-2" data-id="element-271" data-mp="2m3osu11dl0dc">
                                    {shippingOrderId === order.id ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-272" data-mp="3sr2fvpjw2dll" /> Updating…</> : <><Truck className="w-4 h-4" data-id="element-273" data-mp="b8n3ybp54f4p" /> Mark as shipped</>}
                                  </button>}
                                {order.fulfillmentStatus === 'shipped' && <p className="inline-flex items-center gap-2 text-sm font-medium text-[#166534]" data-id="element-274" data-mp="67zf9wodwadn">
                                    <CheckCircle2 className="w-4 h-4" data-id="element-275" data-mp="3olgsh6zvt3fi" /> Fulfillment completed
                                  </p>}
                              </section>
                            </div>
                          </td>
                        </tr>}
                    </React.Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}